/**
 * 群发邮件收件人表格组件
 * 用于在发件箱列表的可展开行和邮件详情抽屉中展示群发邮件的收件人详细状态
 */

import React from "react";
import { Table, Tag, Tooltip, Badge, Space, Typography, Progress } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SyncOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../../../utils/dateUtils";

const { Text } = Typography;

/**
 * 获取收件人状态配置
 * @param {string} status - 收件人状态
 * @returns {Object} 状态配置对象
 */
const getRecipientStatusConfig = (status) => {
    const statusMap = {
        PENDING: { color: "default", text: "待发送", icon: <ClockCircleOutlined />, badgeStatus: "default" },
        SENDING: { color: "processing", text: "发送中", icon: <SyncOutlined spin />, badgeStatus: "processing" },
        SENT: { color: "success", text: "已发送", icon: <CheckCircleOutlined />, badgeStatus: "success" },
        FAILED: { color: "error", text: "发送失败", icon: <CloseCircleOutlined />, badgeStatus: "error" },
        SKIPPED: { color: "warning", text: "已跳过", icon: <ExclamationCircleOutlined />, badgeStatus: "warning" },
    };
    return statusMap[status] || { color: "default", text: status || "未知", icon: null, badgeStatus: "default" };
};

/**
 * 获取收件人类型标签
 * @param {string} type - 收件人类型 (TO, CC, BCC)
 * @returns {Object} 类型配置对象
 */
const getRecipientTypeConfig = (type) => {
    const typeMap = {
        TO: { color: "blue", text: "收件人" },
        CC: { color: "cyan", text: "抄送" },
        BCC: { color: "purple", text: "密送" },
    };
    return typeMap[type] || { color: "default", text: type || "未知" };
};

export const groupRecipientsBySource = (recipients = []) => {
    const groupsBySource = new Map();

    recipients.forEach((recipient) => {
        const sourceGroupName = recipient.source_group_name ?? null;
        if (!groupsBySource.has(sourceGroupName)) {
            groupsBySource.set(sourceGroupName, []);
        }
        groupsBySource.get(sourceGroupName).push(recipient);
    });

    return Array.from(groupsBySource, ([sourceGroupName, groupedRecipients]) => ({
        sourceGroupName,
        recipients: groupedRecipients,
    }));
};

/**
 * 群发邮件收件人表格组件
 * @param {Object} props
 * @param {Array} props.recipients - 收件人列表
 * @param {Object} props.summary - 发送汇总统计
 * @param {boolean} props.loading - 加载状态
 * @param {boolean} props.showSummary - 是否显示汇总统计
 * @param {boolean} props.compact - 是否使用紧凑模式（用于列表展开）
 */
const BulkEmailRecipientsTable = ({ recipients = [], summary = {}, loading = false, showSummary = true, compact = false }) => {
    // 表格列定义
    const columns = [
        {
            title: "序号",
            key: "index",
            width: 50,
            render: (_, __, index) => index + 1,
        },
        {
            title: "收件人邮箱",
            dataIndex: "recipient_email",
            key: "recipient_email",
            ellipsis: true,
            width: compact ? "35%" : "30%",
            render: (email, record) => (
                <Tooltip title={record.recipient_name ? `${record.recipient_name} <${email}>` : email}>
                    <Text style={{ fontSize: compact ? 12 : 13 }}>
                        {record.recipient_name ? (
                            <span>
                                {record.recipient_name} <Text type="secondary">&lt;{email}&gt;</Text>
                            </span>
                        ) : (
                            email
                        )}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: "类型",
            dataIndex: "recipient_type",
            key: "recipient_type",
            width: 70,
            render: (type) => {
                const config = getRecipientTypeConfig(type);
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 90,
            render: (status) => {
                const config = getRecipientStatusConfig(status);
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },
        {
            title: "发送时间",
            dataIndex: "sent_at",
            key: "sent_at",
            width: 140,
            render: (time) => (time ? <Text style={{ fontSize: compact ? 11 : 12 }}>{formatDateTime(time)}</Text> : <Text type="secondary">-</Text>),
        },
        {
            title: "尝试次数",
            dataIndex: "send_attempts",
            key: "send_attempts",
            width: 70,
            align: "center",
            render: (attempts, record) => (
                <Tooltip title={`最大重试次数: ${record.max_attempts || 3}`}>
                    <Text type={attempts > 1 ? "warning" : "secondary"}>{attempts || 0}</Text>
                </Tooltip>
            ),
        },
        {
            title: "错误信息",
            dataIndex: "error_message",
            key: "error_message",
            ellipsis: true,
            render: (error) =>
                error ? (
                    <Tooltip title={error}>
                        <Text type="danger" style={{ fontSize: compact ? 11 : 12 }}>
                            {error}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
    ];

    // 紧凑模式下隐藏部分列
    const displayColumns = compact ? columns.filter((col) => !["send_attempts"].includes(col.key)) : columns;
    const recipientGroups = groupRecipientsBySource(recipients);

    // 计算发送进度
    const totalRecipients = summary.total_recipients || recipients.length || 0;
    const sentCount = summary.sent_count || 0;
    const failedCount = summary.failed_count || 0;
    const pendingCount = summary.pending_count || 0;
    const sendingCount = summary.sending_count || 0;
    const skippedCount = summary.skipped_count || 0;
    const progressPercent = totalRecipients > 0 ? Math.round(((sentCount + failedCount + skippedCount) / totalRecipients) * 100) : 0;

    const renderRecipientsTable = (groupRecipients) => (
        <Table
            dataSource={groupRecipients}
            columns={displayColumns}
            rowKey="id"
            size="small"
            pagination={
                compact
                    ? false
                    : {
                          pageSize: 10,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total) => `共 ${total} 个收件人`,
                      }
            }
            loading={loading}
            scroll={{ x: compact ? 600 : 800 }}
            locale={{ emptyText: "暂无收件人信息" }}
        />
    );

    return (
        <div style={{ padding: compact ? "8px 0" : "12px 0" }}>
            {showSummary && totalRecipients > 0 && (
                <div
                    style={{
                        marginBottom: 12,
                        padding: compact ? "8px 12px" : "12px 16px",
                        background: "linear-gradient(135deg, #f6f8fc 0%, #f0f4ff 100%)",
                        borderRadius: 8,
                        border: "1px solid #e6f0ff",
                    }}
                >
                    <Space size="large" wrap>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                发送进度
                            </Text>
                            <div style={{ width: 200, marginTop: 4 }}>
                                <Progress percent={progressPercent} size="small" status={failedCount > 0 && pendingCount === 0 && sendingCount === 0 ? "exception" : pendingCount > 0 || sendingCount > 0 ? "active" : "success"} />
                            </div>
                        </div>
                        <div>
                            <Badge status="default" text={<Text style={{ fontSize: 13 }}>总计: {totalRecipients}</Text>} />
                        </div>
                        <div>
                            <Badge status="success" text={<Text style={{ fontSize: 13 }}>成功: {sentCount}</Text>} />
                        </div>
                        {sendingCount > 0 && (
                            <div>
                                <Badge status="processing" text={<Text style={{ fontSize: 13 }}>发送中: {sendingCount}</Text>} />
                            </div>
                        )}
                        {failedCount > 0 && (
                            <div>
                                <Badge status="error" text={<Text style={{ fontSize: 13 }}>失败: {failedCount}</Text>} />
                            </div>
                        )}
                        {pendingCount > 0 && (
                            <div>
                                <Badge status="processing" text={<Text style={{ fontSize: 13 }}>待发送: {pendingCount}</Text>} />
                            </div>
                        )}
                        {skippedCount > 0 && (
                            <div>
                                <Badge status="warning" text={<Text style={{ fontSize: 13 }}>已跳过: {skippedCount}</Text>} />
                            </div>
                        )}
                    </Space>
                </div>
            )}

            {recipientGroups.length > 0
                ? recipientGroups.map(({ sourceGroupName, recipients: groupRecipients }) => (
                      <div
                          key={sourceGroupName === null ? "direct-input" : `group-${sourceGroupName}`}
                          style={{ marginBottom: 12 }}
                      >
                          <div
                              style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: compact ? "6px 10px" : "8px 12px",
                                  background: "#fafafa",
                                  border: "1px solid #f0f0f0",
                                  borderRadius: "6px 6px 0 0",
                              }}
                          >
                              <Text strong style={{ fontSize: compact ? 12 : 13 }}>
                                  {sourceGroupName === null ? "直接输入" : sourceGroupName}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                  {groupRecipients.length} 个收件人
                              </Text>
                          </div>
                          {renderRecipientsTable(groupRecipients)}
                      </div>
                  ))
                : renderRecipientsTable([])}
        </div>
    );
};

export default BulkEmailRecipientsTable;
